import { Component } from '@theme/component';

/**
 * Movimiento de Purova. Todo aquí es una mejora: sin este módulo la página se ve completa y quieta.
 *
 *  1. <pv-escena>: la escena del tarro de crema. Corre sus animaciones solo mientras se ve, se inclina
 *     hacia el puntero y gira al bajar por la página.
 *  2. <pv-cinta>: la cinta de frases que se desplaza; su botón la pausa y la reanuda.
 *  3. Entradas: títulos, tarjetas y listas aparecen cuando el visitante llega a ellos.
 *  4. Línea de avance: una línea fina arriba que crece al bajar por la página.
 *
 * En escritorio Horizon desplaza la página dentro de .page-wrapper y no en la ventana, así que aquí se
 * escucha el desplazamiento de cualquier contenedor (fase de captura en document) y se mide la página
 * con su desplazador real.
 *
 * Con «reducir movimiento» no se anima nada. En el editor de temas no hay entradas, para que todo el
 * contenido esté a la vista mientras se edita.
 */

const reducir = window.matchMedia('(prefers-reduced-motion: reduce)');
const enEditor =
  document.documentElement.classList.contains('shopify-design-mode') ||
  Boolean(/** @type {any} */ (window).Shopify?.designMode);

/**
 * @param {number} valor
 * @param {number} minimo
 * @param {number} maximo
 */
const limitar = (valor, minimo, maximo) => Math.min(Math.max(valor, minimo), maximo);

/** El elemento que desplaza la página: .page-wrapper en escritorio, el documento en celulares. */
function desplazador() {
  const envoltura = document.querySelector('.page-wrapper');
  if (envoltura instanceof HTMLElement && /(auto|scroll)/.test(getComputedStyle(envoltura).overflowY)) return envoltura;
  return document.scrollingElement ?? document.documentElement;
}

/** Opciones para escuchar el desplazamiento de cualquier contenedor. */
const EN_CAPTURA = { capture: true, passive: true };

/**
 * Escena del tarro de crema (snippets/pv-tarro.liquid).
 *
 * Escribe en sí misma cuatro variables que usa el CSS de la escena:
 * `--pv-rx` y `--pv-ry` (inclinación), `--pv-p` (avance al bajar, de 0 a 1) y `--pv-giro` (giro del tarro).
 * Con el atributo `data-quieta` solo controla cuándo corren las animaciones.
 *
 * @extends {Component}
 */
class PvEscena extends Component {
  /** @type {AbortController | undefined} */
  #control;
  /** @type {IntersectionObserver | undefined} */
  #observador;
  /** @type {DOMRect | undefined} */
  #caja;
  #cuadro = 0;
  #medida = false;
  #visible = false;
  #meta = { rx: 0, ry: 0, p: 0 };
  #actual = { rx: 0, ry: 0, p: 0 };

  connectedCallback() {
    super.connectedCallback();
    if (reducir.matches) return;

    this.#control?.abort();
    this.#control = new AbortController();
    const { signal } = this.#control;

    this.#observador?.disconnect();
    this.#observador = new IntersectionObserver(
      (entradas) => {
        const ultima = entradas.at(-1);
        if (ultima) this.#alVerse(ultima.isIntersecting);
      },
      { threshold: 0.25 }
    );
    this.#observador.observe(this);

    if (this.hasAttribute('data-quieta')) return;

    document.addEventListener('scroll', this.#medir, { ...EN_CAPTURA, signal });
    window.addEventListener('resize', this.#medir, { passive: true, signal });

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      window.addEventListener('pointermove', this.#seguir, { passive: true, signal });
      document.documentElement.addEventListener('pointerleave', this.#soltar, { passive: true, signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#control?.abort();
    this.#observador?.disconnect();
    cancelAnimationFrame(this.#cuadro);
    this.#cuadro = 0;
    this.#medida = false;
  }

  /**
   * Las animaciones corren solo mientras la escena se ve. Si al cargar la página queda fuera de pantalla,
   * vuelven al inicio y esperan: el tarro se abre (o se cierra) cuando el visitante llega.
   *
   * @param {boolean} visible
   */
  #alVerse(visible) {
    const primera = !this.#medida;
    this.#medida = true;
    this.#visible = visible;

    for (const animacion of this.getAnimations({ subtree: true })) {
      if (visible) {
        // play() reiniciaría una animación que ya terminó: solo se reanudan las que estaban en pausa.
        if (animacion.playState === 'paused') animacion.play();
      } else if (animacion.playState === 'running') {
        animacion.pause();
        if (primera) animacion.currentTime = 0;
      }
    }

    if (visible) this.#medir();
  }

  #medir = () => {
    if (!this.#visible || this.hasAttribute('data-quieta')) return;

    const caja = this.getBoundingClientRect();
    const alto = window.innerHeight || 1;
    this.#caja = caja;
    this.#meta.p = limitar((alto * 0.5 - (caja.top + caja.height / 2)) / (alto * 0.8), 0, 1);
    this.#pedirCuadro();
  };

  /** @param {PointerEvent} evento */
  #seguir = (evento) => {
    if (!this.#visible || evento.pointerType !== 'mouse') return;

    const caja = this.#caja ?? this.getBoundingClientRect();
    const x = (evento.clientX - (caja.left + caja.width / 2)) / (window.innerWidth || 1);
    const y = (evento.clientY - (caja.top + caja.height / 2)) / (window.innerHeight || 1);
    this.#meta.ry = limitar(x * 2, -1, 1);
    this.#meta.rx = limitar(y * -2, -1, 1);
    this.#pedirCuadro();
  };

  #soltar = () => {
    this.#meta.rx = 0;
    this.#meta.ry = 0;
    this.#pedirCuadro();
  };

  #pedirCuadro() {
    if (!this.#cuadro) this.#cuadro = requestAnimationFrame(this.#pintar);
  }

  /** Acerca los valores actuales a su meta con suavidad y los escribe como variables CSS. */
  #pintar = () => {
    this.#cuadro = 0;
    let falta = false;

    for (const clave of /** @type {const} */ (['rx', 'ry', 'p'])) {
      const distancia = this.#meta[clave] - this.#actual[clave];

      if (Math.abs(distancia) > 0.002) {
        this.#actual[clave] += distancia * 0.12;
        falta = true;
      } else {
        this.#actual[clave] = this.#meta[clave];
      }
    }

    const { rx, ry, p } = this.#actual;
    this.style.setProperty('--pv-rx', `${(rx * 7 + p * 14).toFixed(2)}deg`);
    this.style.setProperty('--pv-ry', `${(ry * 12).toFixed(2)}deg`);
    this.style.setProperty('--pv-p', p.toFixed(4));
    this.style.setProperty('--pv-giro', `${(p * 250 + ry * 26).toFixed(2)}deg`);

    if (falta) this.#pedirCuadro();
  };
}

if (!customElements.get('pv-escena')) customElements.define('pv-escena', PvEscena);

/**
 * @typedef {object} CintaRefs
 * @property {HTMLButtonElement} [boton] - Botón que pausa y reanuda la cinta.
 */

/**
 * Cinta de frases en movimiento (sections/pv-cinta.liquid).
 *
 * @extends {Component<CintaRefs>}
 */
class PvCinta extends Component {
  /** Pausa o reanuda el movimiento. Lo llama el botón con `on:click="/alternar"`. */
  alternar() {
    const pausada = this.toggleAttribute('pausada');
    this.refs.boton?.setAttribute('aria-pressed', String(pausada));
  }
}

if (!customElements.get('pv-cinta')) customElements.define('pv-cinta', PvCinta);

/**
 * Entradas al llegar. Cada par es [gesto, selector]; si un elemento coincide con varios, gana el primero.
 * - `unta`: el título aparece de izquierda a derecha, como crema que se extiende.
 * - `crece`: fotos y piezas grandes crecen un poco.
 * - `sube`: el resto sube y aparece; lo que entra a la vez lo hace en fila.
 *
 * @type {ReadonlyArray<readonly [string, string]>}
 */
const ENTRADAS = [
  [
    'unta',
    '.pv-head > .pv-titulo, .pv-marca__intro > .pv-titulo, .pv-faq__intro > .pv-titulo, .pv-cierre__texto > .pv-titulo, .pv-dest__contenido > .pv-titulo, .pv-combo__texto > .pv-titulo',
  ],
  ['crece', '.pv-dest__media, .pv-combo__fotos'],
  [
    'sube',
    '.pv-head > *, .pv-marca__intro > *, .pv-faq__intro > *, .pv-cierre__texto > *, .pv-cierre__acciones > *, .pv-dest__contenido > *, .pv-combo__texto > *, .pv-combo__form, .pv-cats__item, .pv-ritual__paso, .pv-ritual__nota, .pv-pasos__paso, .pv-marca__principio, .pv-guias__item, .pv-guias__todas, .pv-faq__item, .pv-confianza__item, .resource-list__item, .product-grid__item',
  ],
];

/** Lo que tiene movimiento continuo y debe detenerse cuando no se ve. */
const CONTINUOS = 'pv-cinta, .pv-dest__media, .pv-combo__fotos, .pv-cierre';

/** Elementos que aún esperan su entrada. @type {Set<HTMLElement>} */
const pendientes = new Set();

/**
 * @param {HTMLElement} elemento
 * @param {number} orden - Posición dentro del grupo que entra a la vez; retrasa la entrada.
 */
function mostrar(elemento, orden) {
  if (!pendientes.delete(elemento)) return;

  elemento.style.setProperty('--pv-espera', `${Math.min(orden, 6) * 80}ms`);
  elemento.dataset.pvEntra = 'ya';

  // Al terminar, el elemento vuelve a sus estilos propios (transiciones al pasar el cursor, etc.).
  window.setTimeout(() => {
    delete elemento.dataset.pvEntra;
    delete elemento.dataset.pvGesto;
    elemento.style.removeProperty('--pv-espera');
  }, 1900 + Math.min(orden, 6) * 80);
}

function prepararEntradas() {
  // Solo el contenido de la página: cajones, ventanas y cabecera quedan fuera.
  const contenido = document.querySelector('main');
  if (!contenido) return;

  const selector = ENTRADAS.map(([, lista]) => lista).join(', ');
  const borde = window.innerHeight * 0.92;

  const observador = new IntersectionObserver(
    (entradas) => {
      let orden = 0;

      for (const entrada of entradas) {
        if (!entrada.isIntersecting || !(entrada.target instanceof HTMLElement)) continue;
        observador.unobserve(entrada.target);
        mostrar(entrada.target, orden++);
      }
    },
    { rootMargin: '0px 0px -6% 0px' }
  );

  for (const elemento of contenido.querySelectorAll(selector)) {
    if (!(elemento instanceof HTMLElement)) continue;
    // Lo que ya se ve al cargar no se toca: solo espera lo que está más abajo.
    if (elemento.getBoundingClientRect().top < borde) continue;

    const gesto = ENTRADAS.find(([, lista]) => elemento.matches(lista))?.[0] ?? 'sube';
    elemento.dataset.pvGesto = gesto;
    elemento.dataset.pvEntra = 'espera';
    pendientes.add(elemento);
    observador.observe(elemento);
  }

  if (!pendientes.size) return;

  // Red de seguridad: nada se queda oculto si el foco entra en ello o si el visitante ya lo tiene delante.
  document.addEventListener('focusin', (evento) => {
    if (!(evento.target instanceof Element)) return;
    const oculto = evento.target.closest('[data-pv-entra="espera"]');
    if (oculto instanceof HTMLElement) mostrar(oculto, 0);
  });

  let temporizador = 0;

  document.addEventListener(
    'scroll',
    () => {
      if (!pendientes.size) return;
      window.clearTimeout(temporizador);
      temporizador = window.setTimeout(() => {
        const alto = window.innerHeight;
        const ancho = window.innerWidth;
        const pie = document.querySelector('footer');
        const alFinal = pie instanceof HTMLElement && pie.getBoundingClientRect().top < alto;
        let orden = 0;

        for (const elemento of [...pendientes]) {
          const caja = elemento.getBoundingClientRect();
          const aLaVista = caja.top < alto && caja.bottom > 0 && caja.left < ancho && caja.right > 0;
          if (alFinal || aLaVista) mostrar(elemento, orden++);
        }
      }, 260);
    },
    EN_CAPTURA
  );
}

/** Línea de avance de lectura: crece de 0 a 1 según lo recorrido de la página. */
function prepararAvance() {
  const linea = document.createElement('div');
  linea.className = 'pv-avance';
  linea.setAttribute('aria-hidden', 'true');
  document.body.append(linea);

  let pagina = desplazador();
  let cuadro = 0;

  const pintar = () => {
    cuadro = 0;
    const recorrido = pagina.scrollHeight - pagina.clientHeight;
    const avance = recorrido > 0 ? limitar(pagina.scrollTop / recorrido, 0, 1) : 0;
    linea.style.transform = `scaleX(${avance.toFixed(4)})`;
  };
  const pedir = () => {
    if (!cuadro) cuadro = requestAnimationFrame(pintar);
  };

  document.addEventListener(
    'scroll',
    (evento) => {
      if (evento.target === document || evento.target === pagina) pedir();
    },
    EN_CAPTURA
  );
  window.addEventListener(
    'resize',
    () => {
      pagina = desplazador();
      pedir();
    },
    { passive: true }
  );
  pedir();
}

function prepararContinuos() {
  const observador = new IntersectionObserver((entradas) => {
    for (const entrada of entradas) entrada.target.toggleAttribute('data-pv-fuera', !entrada.isIntersecting);
  });

  for (const elemento of document.querySelectorAll(CONTINUOS)) observador.observe(elemento);
}

if (!reducir.matches) {
  prepararContinuos();
  prepararAvance();
  if (!enEditor) prepararEntradas();
}